/*
 * Recently viewed products.
 *
 * Two jobs, both driven from this one file so the behavior lives in one place:
 *   1. On a product page, record the handle in localStorage.
 *   2. Where the section is present, fetch a real card for each saved handle.
 *
 * Cards are requested with ?section_id= so they use the same card-product
 * snippet as the rest of the theme rather than markup rebuilt in JavaScript.
 * History is stored on this device; card requests send handles to this store.
 *
 * Wrapped in an IIFE because both the tracker snippet and the section include
 * this file, so on a product page holding the section it executes twice.
 */
(function () {
  const STORAGE_KEY = 'recently-viewed-products';
  const MAX_STORED = 12;
  const validHandle = (handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle);
  let instance = 0;

  function readHandles() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? [...new Set(parsed.filter(validHandle))].slice(0, MAX_STORED) : [];
    } catch (error) {
      return [];
    }
  }

  function recordHandle(handle) {
    if (!validHandle(handle)) return;

    try {
      // Most-recent first, no duplicates, capped so storage cannot grow forever.
      const handles = readHandles().filter((existing) => existing !== handle);
      handles.unshift(handle);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
    } catch (error) {
      // Storage unavailable (private mode, quota). The feature just stays empty.
    }
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      // In the theme editor the section renders placeholder cards so it is
      // visible and measurable; leave those alone rather than appending real
      // history on top of them.
      if (window.Shopify && window.Shopify.designMode) return;

      this.grid = this.querySelector('[data-recently-viewed-grid]');
      if (!this.grid) return;

      this.instanceId ||= ++instance;
      this.limit = Math.min(MAX_STORED, Math.max(1, parseInt(this.dataset.limit, 10) || 4));
      this.observer?.disconnect();
      if (typeof IntersectionObserver === 'undefined') {
        this.render();
        return;
      }
      // Hidden custom elements have no box: observe the visible Shopify section wrapper.
      this.observer = new IntersectionObserver(entries => {
        if (!this.isConnected || !this.observer || !entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.observer = null;
        this.render();
      }, { rootMargin: '600px 0px' });
      this.observer.observe(this.parentElement || this);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.observer = null;
    }

    async render() {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      let handles = readHandles();

      // On a product page the product being looked at is not "recently viewed".
      if (this.dataset.excludeCurrent === 'true' && this.dataset.currentHandle) {
        handles = handles.filter((handle) => handle !== this.dataset.currentHandle);
      }

      handles = handles.slice(0, this.limit);
      if (!handles.length) {
        this.grid.replaceChildren();
        this.hidden = true;
        return;
      }

      const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle, controller.signal)));
      if (!this.isConnected || this.controller !== controller) return;
      const found = cards.filter(Boolean);

      // Built off-document so the grid is only touched once.
      const fragment = document.createDocumentFragment();
      found.forEach((markup) => {
        const item = document.createElement('li');
        item.className = 'recently-viewed__item grid__item';
        item.innerHTML = markup;
        fragment.appendChild(item);
      });

      this.grid.replaceChildren(fragment);
      this.hidden = found.length === 0;
      this.querySelector?.('carousel-scrollbar')?.refresh?.();
    }

    async fetchCard(handle, signal) {
      // The root URL comes from Liquid so localized storefronts (/en-gb/...)
      // resolve correctly.
      const root = this.dataset.rootUrl || '/';
      const base = root.endsWith('/') ? root : `${root}/`;

      try {
        const response = await fetch(`${base}products/${encodeURIComponent(handle)}?section_id=recently-viewed-card`, { signal });

        // A deleted or unpublished product 404s; skip it rather than failing the lot.
        if (!response.ok) return null;

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const card = html.querySelector('[data-recently-viewed-card]');
        if (!card || card.dataset.recentlyViewedCard !== handle) return null;
        const ids = new Map();
        card.querySelectorAll('[id]').forEach((node) => {
          const original = node.id;
          node.id = `${original}-recent-${this.instanceId}`;
          ids.set(original, node.id);
        });
        card.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [for]').forEach((node) => {
          ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].forEach((attribute) => {
            const value = node.getAttribute(attribute);
            if (value) node.setAttribute(attribute, value.split(/\s+/).map((id) => ids.get(id) || id).join(' '));
          });
        });
        return card.innerHTML;
      } catch (error) {
        return null;
      }
    }
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }

  // Record the current product. Runs on every product page, whether or not the
  // section itself is on that page.
  const tracker = document.getElementById('RecentlyViewedTracker');
  if (tracker) recordHandle(tracker.dataset.productHandle);
})();
